import { useState } from 'react'
import { router } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { getDateLocale } from '@/lib/i18n'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Link } from '@inertiajs/react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog'
import { Label } from '../../../components/ui/label'
import { Input } from '../../../components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select'
import { getTypeBadge, getDetails, ALL_TRANSACTION_TYPES, Transaction } from '../../../components/credit-transactions/transaction-helpers'

interface Member {
  id: number
  name: string
  email: string
  organization_role: string
  admin: boolean
      super_admin?: boolean
  created_at: string
}

interface Project {
  id: number
  name: string
  description: string | null
  created_at: string
  owner_name: string
}

interface Organization {
  id: number
  name: string
  description: string | null
  created_at: string
  updated_at: string
  current_plan: string
  plan_display_name: string
  plan_credits: number
  credits_purchased: number
  credits_used: number
  credits_available: number
  internal: boolean
  default_ai_model: string | null
  default_pm_model: string | null
}

interface Subscription {
  plan_name: string
  status: string
  billing_interval: string
  current_period_start: string | null
  current_period_end: string | null
  cancel_at_period_end: boolean
  canceled_at: string | null
  stripe_subscription_id: string
}

interface SubscriptionHistoryEntry {
  date: string
  change_type: 'plan_change' | 'status_change'
  from: string
  to: string
}

interface TransactionPagy {
  page: number
  count: number
  pages: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface AdminOrganizationShowProps {
  organization: Organization
  ai_models: Record<string, string>
  default_ai_model: string
  pm_models: Record<string, string>
  default_pm_model: string
  subscription: Subscription | null
  subscription_history: SubscriptionHistoryEntry[]
  members: Member[]
  projects: Project[]
  transactions: Transaction[]
  transactions_pagy: TransactionPagy
  transaction_filters: { type: string | null }
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
    notice?: string
  }
}

function Show({ organization, ai_models, default_ai_model, pm_models, default_pm_model, subscription, subscription_history, members, projects, transactions, transactions_pagy, transaction_filters }: AdminOrganizationShowProps) {
  const { t } = useTranslation('admin')
  const { t: tOrg } = useTranslation('organizations')
  const [txTypeFilter, setTxTypeFilter] = useState(transaction_filters?.type || 'all')
  const [showGrantCreditsDialog, setShowGrantCreditsDialog] = useState(false)
  const [creditsAmount, setCreditsAmount] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showInternalDialog, setShowInternalDialog] = useState(false)
  const [isTogglingInternal, setIsTogglingInternal] = useState(false)
  const [isUpdatingModel, setIsUpdatingModel] = useState(false)
  const [isUpdatingPmModel, setIsUpdatingPmModel] = useState(false)

  const handleTxFilter = () => {
    const params: Record<string, string> = {}
    if (txTypeFilter !== 'all') params.transaction_type = txTypeFilter
    router.get(`/admin/organizations/${organization.id}`, params, { preserveState: true })
  }

  const handleTxPage = (page: number) => {
    const params = new URLSearchParams(window.location.search)
    params.set('transactions_page', page.toString())
    router.get(`${window.location.pathname}?${params.toString()}`, {}, { preserveState: true })
  }

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(getDateLocale(), {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })

  const handleModelChange = (value: string) => {
    setIsUpdatingModel(true)
    router.patch(`/admin/organizations/${organization.id}/update_default_ai_model`, {
      default_ai_model: value === 'default' ? '' : value
    }, {
      preserveScroll: true,
      onFinish: () => setIsUpdatingModel(false)
    })
  }

  const handlePmModelChange = (value: string) => {
    setIsUpdatingPmModel(true)
    router.patch(`/admin/organizations/${organization.id}/update_pm_model`, {
      default_pm_model: value === 'default' ? '' : value
    }, {
      preserveScroll: true,
      onFinish: () => setIsUpdatingPmModel(false)
    })
  }

  const handleToggleInternal = () => {
    setIsTogglingInternal(true)
    router.post(`/admin/organizations/${organization.id}/toggle_internal`, {}, {
      onSuccess: () => {
        setShowInternalDialog(false)
      },
      onFinish: () => setIsTogglingInternal(false)
    })
  }

  const handleGrantCredits = () => {
    if (!creditsAmount || parseInt(creditsAmount) <= 0) return

    setIsSubmitting(true)
    router.post(`/admin/organizations/${organization.id}/grant_credits`, {
      credits_amount: creditsAmount
    }, {
      onSuccess: () => {
        setShowGrantCreditsDialog(false)
        setCreditsAmount('')
      },
      onFinish: () => setIsSubmitting(false)
    })
  }

  return (
    <>
      <AppHeader breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: t('organizations.index.title'), href: '/admin/organizations' }, { label: t('organizations.show.title') }]} />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">
        {/* Page Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              {organization.name}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('organizations.show.description')}
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link href="/admin/organizations">{t('organizations.show.backToOrganizations')}</Link>
          </Button>
        </div>

        <div className="space-y-6">
          {/* Organization Information */}
          <Card className="shadow-none">
            <CardHeader>
              <CardTitle>{t('organizations.show.info.title')}</CardTitle>
              <CardDescription>{t('organizations.show.info.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 flex-shrink-0 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-semibold text-xl">
                  {organization.name.substring(0, 2).toUpperCase()}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-foreground">
                    {organization.name}
                  </h2>
                  {organization.description && (
                    <p className="text-sm text-muted-foreground">{organization.description}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('organizations.show.info.totalMembers')}</p>
                  <p className="mt-1 text-lg font-semibold text-foreground">
                    {members.length}
                  </p>
                </div>

                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('organizations.show.info.status')}</p>
                  <div className="mt-1">
                    <Badge variant="success">
                      {t('organizations.show.badges.active')}
                    </Badge>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('organizations.show.info.created')}</p>
                  <p className="mt-1 text-sm text-foreground">
                    {new Date(organization.created_at).toLocaleDateString(getDateLocale(), {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>

                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('organizations.show.info.lastUpdated')}</p>
                  <p className="mt-1 text-sm text-foreground">
                    {new Date(organization.updated_at).toLocaleDateString(getDateLocale(), {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>

              {/* Subscription & Credits Section */}
              <div className="pt-4 border-t space-y-4">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-muted-foreground">{t('organizations.show.credits.currentPlan')}</p>
                  <Badge variant={organization.current_plan === 'free' ? 'secondary' : 'default'} className="text-sm">
                    {organization.plan_display_name}
                  </Badge>
                </div>

                {/* Credits Cards */}
                <div className="grid grid-cols-2 gap-2">
                  {/* Plan Credits */}
                  <div className="bg-muted/50 border border-border rounded p-2.5">
                    <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-0.5">
                      {t('organizations.show.credits.planCredits')}
                    </p>
                    <p className="text-lg font-bold text-foreground">
                      {organization.plan_credits.toLocaleString()}
                    </p>
                  </div>

                  {/* Credits Purchased */}
                  <div className="bg-muted/50 border border-border rounded p-2.5">
                    <div className="flex items-center justify-between mb-0.5">
                      <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                        {t('organizations.show.credits.creditsPurchased')}
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowGrantCreditsDialog(true)}
                        className="h-6 px-2 text-[11px] font-medium border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                      >
                        {t('organizations.show.credits.addCredits')}
                      </Button>
                    </div>
                    <p className="text-base font-semibold text-foreground">
                      {organization.credits_purchased.toLocaleString()}
                    </p>
                  </div>

                  {/* Credits Available - Highlighted */}
                  <div className="bg-primary/5 border border-primary/20 rounded p-2.5">
                    <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-0.5">
                      {t('organizations.show.credits.creditsAvailable')}
                    </p>
                    <p className="text-lg font-bold text-primary">
                      {organization.credits_available.toLocaleString()}
                    </p>
                  </div>

                  {/* Credits Used */}
                  <div className="bg-muted/50 border border-border rounded p-2.5">
                    <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-0.5">
                      {t('organizations.show.credits.creditsUsed')}
                    </p>
                    <p className="text-base font-semibold text-green-600">
                      {organization.credits_used.toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Internal Organization Section */}
                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground">{t('organizations.show.internal.title')}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {t('organizations.show.internal.description')}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge
                        variant={organization.internal ? 'default' : 'secondary'}
                        className={organization.internal ? 'bg-amber-500 hover:bg-amber-500' : ''}
                      >
                        {organization.internal
                          ? t('organizations.show.internal.enabled')
                          : t('organizations.show.internal.disabled')}
                      </Badge>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowInternalDialog(true)}
                      >
                        {t('organizations.show.internal.toggle')}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* AI Model Settings */}
          <Card className="shadow-none">
            <CardHeader>
              <CardTitle>{t('organizations.show.modelSettings.title')}</CardTitle>
              <CardDescription>{t('organizations.show.modelSettings.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>{t('organizations.show.defaultModel.title')}</Label>
                <Select
                  value={organization.default_ai_model || 'default'}
                  onValueChange={handleModelChange}
                  disabled={isUpdatingModel}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">
                      {t('organizations.show.defaultModel.systemDefault', { model: ai_models[default_ai_model] || default_ai_model })}
                    </SelectItem>
                    {Object.entries(ai_models).map(([modelId, modelName]) => (
                      <SelectItem key={modelId} value={modelId}>
                        {modelName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {organization.default_ai_model && (
                  <p className="text-xs text-muted-foreground">
                    {t('organizations.show.defaultModel.overrideActive')}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>{t('organizations.show.pmModel.title')}</Label>
                <Select
                  value={organization.default_pm_model || 'default'}
                  onValueChange={handlePmModelChange}
                  disabled={isUpdatingPmModel}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">
                      {t('organizations.show.pmModel.systemDefault', { model: pm_models[default_pm_model] || default_pm_model })}
                    </SelectItem>
                    {Object.entries(pm_models).map(([modelId, modelName]) => (
                      <SelectItem key={modelId} value={modelId}>
                        {modelName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {organization.default_pm_model && (
                  <p className="text-xs text-muted-foreground">
                    {t('organizations.show.pmModel.overrideActive')}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Subscription */}
          <Card className="shadow-none">
            <CardHeader>
              <CardTitle>{t('organizations.show.subscription.title')}</CardTitle>
              <CardDescription>{t('organizations.show.subscription.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {subscription ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('organizations.show.subscription.plan')}</p>
                      <div className="mt-1">
                        <Badge variant="default" className="capitalize">{subscription.plan_name}</Badge>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('organizations.show.subscription.status')}</p>
                      <div className="mt-1">
                        <Badge variant={
                          subscription.status === 'active' ? 'success' :
                          subscription.status === 'past_due' ? 'warning' :
                          'destructive'
                        }>
                          {t(`organizations.show.subscription.statuses.${subscription.status}`)}
                        </Badge>
                        {subscription.cancel_at_period_end && (
                          <span className="ml-2 text-xs text-amber-600">
                            {t('organizations.show.subscription.cancelAtPeriodEnd')}
                          </span>
                        )}
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('organizations.show.subscription.billingInterval')}</p>
                      <p className="mt-1 text-sm text-foreground">
                        {subscription.billing_interval === 'month'
                          ? t('organizations.show.subscription.monthly')
                          : t('organizations.show.subscription.yearly')}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('organizations.show.subscription.currentPeriod')}</p>
                      <p className="mt-1 text-sm text-foreground">
                        {subscription.current_period_start && subscription.current_period_end
                          ? `${new Date(subscription.current_period_start).toLocaleDateString(getDateLocale(), { month: 'short', day: 'numeric' })} – ${new Date(subscription.current_period_end).toLocaleDateString(getDateLocale(), { month: 'short', day: 'numeric', year: 'numeric' })}`
                          : '–'}
                      </p>
                    </div>
                  </div>

                  <div className="pt-2">
                    <a
                      href={`https://dashboard.stripe.com/subscriptions/${subscription.stripe_subscription_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {t('organizations.show.subscription.stripeLink')} &rarr;
                    </a>
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground py-2">{t('organizations.show.subscription.noSubscription')}</p>
              )}

              {/* Plan History */}
              <div className="pt-4 border-t">
                <h4 className="text-sm font-medium text-foreground mb-3">{t('organizations.show.subscription.history.title')}</h4>
                {subscription_history.length > 0 ? (
                  <div className="space-y-2">
                    {subscription_history.map((entry, index) => (
                      <div key={index} className="flex items-center gap-3 text-sm">
                        <span className="text-muted-foreground whitespace-nowrap text-xs">
                          {new Date(entry.date).toLocaleDateString(getDateLocale(), {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                        <span className="text-foreground">
                          {entry.change_type === 'plan_change'
                            ? t('organizations.show.subscription.history.planChange', { from: entry.from, to: entry.to })
                            : t('organizations.show.subscription.history.statusChange', { from: entry.from, to: entry.to })}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('organizations.show.subscription.history.noHistory')}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Members */}
          <Card className="shadow-none">
            <CardHeader>
              <CardTitle>{t('organizations.show.members.title', { count: members.length })}</CardTitle>
              <CardDescription>{t('organizations.show.members.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              {members.length > 0 ? (
                <div className="bg-card rounded-lg border border-border overflow-hidden overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {t('organizations.show.members.table.name')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {t('organizations.show.members.table.email')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {t('organizations.show.members.table.role')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {t('organizations.show.members.table.joined')}
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {t('organizations.show.members.table.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-border">
                      {members.map((member) => (
                        <tr key={member.id} className="hover:bg-muted transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-foreground">{member.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-foreground">{member.email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge variant={member.organization_role === 'external' ? 'outline' : 'secondary'}>
                              {member.admin ? t('organizations.show.badges.admin') : member.organization_role === 'external' ? t('organizations.show.badges.external') : t('organizations.show.badges.member')}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-foreground">
                              {new Date(member.created_at).toLocaleDateString(getDateLocale(), {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <a
                              href={`/admin/users/${member.id}`}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              {t('organizations.show.members.view')}
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">{t('organizations.show.members.empty')}</p>
              )}
            </CardContent>
          </Card>

          {/* Projects */}
          <Card className="shadow-none">
            <CardHeader>
              <CardTitle>{t('organizations.show.projects.title', { count: projects.length })}</CardTitle>
              <CardDescription>{t('organizations.show.projects.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              {projects.length > 0 ? (
                <div className="bg-card rounded-lg border border-border overflow-hidden overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {t('organizations.show.projects.table.name')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {t('organizations.show.projects.table.description')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {t('organizations.show.projects.table.created')}
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {t('organizations.show.projects.table.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-border">
                      {projects.map((project) => (
                        <tr key={project.id} className="hover:bg-muted transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-foreground">{project.name}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-foreground max-w-xs truncate">
                              {project.description || '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-foreground">
                              {new Date(project.created_at).toLocaleDateString(getDateLocale(), {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <a
                              href={`/admin/projects/${project.id}`}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              {t('organizations.show.projects.view')}
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">{t('organizations.show.projects.empty')}</p>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle>{t('organizations.show.transactions.title')}</CardTitle>
                  <CardDescription>{t('organizations.show.transactions.description')}</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={txTypeFilter} onValueChange={setTxTypeFilter}>
                    <SelectTrigger className="h-8 text-xs w-[160px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('organizations.show.transactions.allTypes')}</SelectItem>
                      {ALL_TRANSACTION_TYPES.map(type => (
                        <SelectItem key={type} value={type}>{tOrg(`creditTransactions.types.${type}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" className="h-8 text-xs" onClick={handleTxFilter}>
                    {t('organizations.show.transactions.filterLabel')}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {transactions.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            {t('organizations.show.transactions.table.date')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            {t('organizations.show.transactions.table.type')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            {t('organizations.show.transactions.table.amount')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            {t('organizations.show.transactions.table.project')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            {t('organizations.show.transactions.table.details')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            {t('organizations.show.transactions.table.user')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {transactions.map(txn => (
                          <tr key={txn.id} className="hover:bg-muted/50 transition-colors">
                            <td className="px-4 py-3 text-xs whitespace-nowrap text-muted-foreground">
                              {formatDate(txn.created_at)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {getTypeBadge(txn.transaction_type, tOrg)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`text-sm font-semibold ${txn.credits_amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                {txn.credits_amount < 0 ? '' : '+'}{txn.credits_amount.toFixed(4)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs whitespace-nowrap">
                              {txn.creditable_type === 'Project' && txn.creditable_id ? (
                                <a href={`/admin/projects/${txn.creditable_id}`} className="text-blue-600 hover:text-blue-900">
                                  {txn.creditable_name || `#${txn.creditable_id}`}
                                </a>
                              ) : '—'}
                            </td>
                            <td className="px-4 py-3 text-xs text-muted-foreground">
                              {getDetails(txn, tOrg)}
                            </td>
                            <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                              {txn.user_email || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {transactions_pagy.pages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t text-xs text-muted-foreground">
                      <span>
                        {t('organizations.show.transactions.pagination.showing', {
                          from: transactions_pagy.from,
                          to: transactions_pagy.to,
                          count: transactions_pagy.count,
                        })}
                      </span>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          disabled={!transactions_pagy.prev}
                          onClick={() => handleTxPage(transactions_pagy.prev!)}
                        >
                          {t('organizations.show.transactions.pagination.previous')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          disabled={!transactions_pagy.next}
                          onClick={() => handleTxPage(transactions_pagy.next!)}
                        >
                          {t('organizations.show.transactions.pagination.next')}
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-center text-muted-foreground py-8 text-sm">{t('organizations.show.transactions.empty')}</p>
              )}
            </CardContent>
          </Card>
        </div>
            </div>
          </div>
        </div>
      </div>

      {/* Grant Credits Dialog */}
      <Dialog open={showGrantCreditsDialog} onOpenChange={setShowGrantCreditsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('organizations.show.grantCreditsDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('organizations.show.grantCreditsDialog.description', { name: organization.name })}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="credits_amount">{t('organizations.show.grantCreditsDialog.creditsAmount')}</Label>
              <Input
                id="credits_amount"
                type="number"
                placeholder={t('organizations.show.grantCreditsDialog.enterAmount')}
                value={creditsAmount}
                onChange={(e) => setCreditsAmount(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleGrantCredits()}
                min="1"
              />
              <p className="text-xs text-muted-foreground">
                {t('organizations.show.grantCreditsDialog.currentBalance', { amount: organization.credits_available.toLocaleString() })}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGrantCreditsDialog(false)}>
              {t('organizations.show.grantCreditsDialog.cancel')}
            </Button>
            <Button onClick={handleGrantCredits} disabled={isSubmitting || !creditsAmount || parseInt(creditsAmount) <= 0}>
              {isSubmitting ? t('organizations.show.grantCreditsDialog.granting') : t('organizations.show.grantCreditsDialog.grant')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Toggle Internal Dialog */}
      <Dialog open={showInternalDialog} onOpenChange={setShowInternalDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {organization.internal
                ? t('organizations.show.toggleInternalDialog.disableTitle')
                : t('organizations.show.toggleInternalDialog.enableTitle')}
            </DialogTitle>
            <DialogDescription>
              {organization.internal
                ? t('organizations.show.toggleInternalDialog.disableDescription', { name: organization.name })
                : t('organizations.show.toggleInternalDialog.enableDescription', { name: organization.name })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInternalDialog(false)}>
              {t('organizations.show.toggleInternalDialog.cancel')}
            </Button>
            <Button
              onClick={handleToggleInternal}
              disabled={isTogglingInternal}
              variant={organization.internal ? 'destructive' : 'default'}
            >
              {isTogglingInternal
                ? t('organizations.show.internal.toggling')
                : organization.internal
                  ? t('organizations.show.toggleInternalDialog.disable')
                  : t('organizations.show.toggleInternalDialog.enable')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

Show.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Show
